// Sticker request page: choose paper format, choose self-print (instant PDF
// via e-mail) or postal delivery (address form).

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    Printer,
    Mail,
    Truck,
    Check,
    Loader2,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { FirstAidSymbol } from "@/components/FirstAidSymbol";
import { LanguagePicker } from "@/components/LanguagePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORMATS = ["A2", "A3", "A4"];

export default function StickerRequestPage() {
    const { t, lang } = useI18n();

    // 'choose-format' | 'choose-method' | 'self-print' | 'self-print-done'
    //  'ship-form'    | 'ship-done'
    const [stage, setStage] = useState("choose-format");
    const [format, setFormat] = useState("A4");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        document.title = "Aufkleber anfordern | SafetyAssist";
    }, []);

    const continueToMethod = () => {
        setErr("");
        setStage("choose-method");
    };

    return (
        <div className="min-h-screen bg-zinc-50 flex flex-col">
            <Header />

            <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <Link
                    to="/"
                    data-testid="anforderung-back"
                    className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition mb-6"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t("anforderung_back")}
                </Link>

                <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900 leading-tight">
                    {t("anforderung_title")}
                </h1>
                <p className="mt-3 text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
                    {t("anforderung_subtitle")}
                </p>

                <Stepper stage={stage} />

                {stage === "choose-format" && (
                    <section
                        className="mt-8 bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm"
                        data-testid="format-step"
                    >
                        <h2 className="font-display font-bold text-xl sm:text-2xl text-zinc-900">
                            {t("anforderung_format_title")}
                        </h2>
                        <p className="mt-2 text-sm sm:text-base text-zinc-600">
                            {t("anforderung_format_sub")}
                        </p>

                        <div className="mt-5 grid grid-cols-3 gap-3">
                            {FORMATS.map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFormat(f)}
                                    data-testid={`format-${f}`}
                                    className={`rounded-xl px-3 py-5 sm:py-7 text-center transition border-2 ${
                                        format === f
                                            ? "border-[#245288] bg-[#245288] text-white shadow-lg"
                                            : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                                    }`}
                                >
                                    <div className="font-display font-black text-2xl sm:text-3xl">
                                        {f}
                                    </div>
                                    <div
                                        className={`text-[11px] sm:text-xs mt-1 ${
                                            format === f
                                                ? "text-white/80"
                                                : "text-zinc-500"
                                        }`}
                                    >
                                        {t(`anforderung_format_${f}_desc`)}
                                    </div>
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={continueToMethod}
                            data-testid="format-continue"
                            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-display font-bold px-5 py-3 rounded-xl transition"
                        >
                            {t("anforderung_format_continue")}
                        </button>
                    </section>
                )}

                {stage === "choose-method" && (
                    <section
                        className="mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5"
                        data-testid="method-step"
                    >
                        <button
                            data-testid="method-self-print"
                            onClick={() => setStage("self-print")}
                            className="text-left bg-white border-2 border-zinc-200 hover:border-[#245288] rounded-2xl p-6 transition shadow-sm hover:shadow-md"
                        >
                            <div className="w-12 h-12 rounded-xl bg-[#245288]/10 text-[#245288] flex items-center justify-center">
                                <Printer className="w-6 h-6" strokeWidth={2.2} />
                            </div>
                            <h3 className="mt-4 font-display font-black text-lg sm:text-xl text-zinc-900 leading-tight">
                                {t("anforderung_method_self_title")}
                            </h3>
                            <p className="mt-2 text-sm sm:text-base text-zinc-600 leading-relaxed">
                                {t("anforderung_method_self_body")}
                            </p>
                            <div className="mt-3 text-xs uppercase tracking-wider font-semibold text-[#245288]">
                                {t("anforderung_format_chosen")}: {format}
                            </div>
                        </button>

                        <button
                            data-testid="method-ship"
                            onClick={() => setStage("ship-form")}
                            className="text-left bg-white border-2 border-zinc-200 hover:border-emerald-600 rounded-2xl p-6 transition shadow-sm hover:shadow-md"
                        >
                            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                <Truck className="w-6 h-6" strokeWidth={2.2} />
                            </div>
                            <h3 className="mt-4 font-display font-black text-lg sm:text-xl text-zinc-900 leading-tight">
                                {t("anforderung_method_ship_title")}
                            </h3>
                            <p className="mt-2 text-sm sm:text-base text-zinc-600 leading-relaxed">
                                {t("anforderung_method_ship_body")}
                            </p>
                            <div className="mt-3 text-xs uppercase tracking-wider font-semibold text-emerald-700">
                                {t("anforderung_format_chosen")}: {format}
                            </div>
                        </button>
                    </section>
                )}

                {stage === "self-print" && (
                    <SelfPrintForm
                        format={format}
                        lang={lang}
                        busy={busy}
                        setBusy={setBusy}
                        err={err}
                        setErr={setErr}
                        onDone={() => setStage("self-print-done")}
                        onBack={() => setStage("choose-method")}
                    />
                )}

                {stage === "self-print-done" && (
                    <SuccessCard
                        testid="self-print-done"
                        title={t("anforderung_self_done_title")}
                        body={t("anforderung_self_done_body")}
                    />
                )}

                {stage === "ship-form" && (
                    <ShipForm
                        format={format}
                        lang={lang}
                        busy={busy}
                        setBusy={setBusy}
                        err={err}
                        setErr={setErr}
                        onDone={() => setStage("ship-done")}
                        onBack={() => setStage("choose-method")}
                    />
                )}

                {stage === "ship-done" && (
                    <SuccessCard
                        testid="ship-done"
                        title={t("anforderung_ship_done_title")}
                        body={t("anforderung_ship_done_body")}
                    />
                )}
            </main>
        </div>
    );
}

const Stepper = ({ stage }) => {
    const labels = ["format", "method", "details"];
    let active = 0;
    if (stage.startsWith("choose-method")) active = 1;
    if (
        stage === "self-print" ||
        stage === "ship-form" ||
        stage === "self-print-done" ||
        stage === "ship-done"
    )
        active = 2;
    return (
        <ol className="mt-6 flex items-center gap-2 text-xs sm:text-sm">
            {labels.map((l, i) => (
                <React.Fragment key={l}>
                    <li
                        className={`flex items-center gap-1.5 ${
                            i <= active ? "text-zinc-900" : "text-zinc-400"
                        }`}
                    >
                        <span
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                                i < active
                                    ? "bg-emerald-600 text-white"
                                    : i === active
                                      ? "bg-zinc-900 text-white"
                                      : "bg-zinc-200 text-zinc-500"
                            }`}
                        >
                            {i < active ? (
                                <Check className="w-3 h-3" strokeWidth={3.5} />
                            ) : (
                                i + 1
                            )}
                        </span>
                        <span className="hidden sm:inline">{l}</span>
                    </li>
                    {i < labels.length - 1 && (
                        <li className="w-6 h-px bg-zinc-300" />
                    )}
                </React.Fragment>
            ))}
        </ol>
    );
};

const SelfPrintForm = ({ format, lang, busy, setBusy, err, setErr, onDone, onBack }) => {
    const { t } = useI18n();
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!EMAIL_RE.test(email.trim())) {
            setErr(t("anforderung_error_email"));
            return;
        }
        if (!consent) {
            setErr(t("anforderung_error_consent"));
            return;
        }
        setBusy(true);
        try {
            // Server returns the personalised PDF as a binary stream.
            const res = await axios.post(
                `${API}/sticker-requests/print`,
                {
                    email: email.trim(),
                    format,
                    locale: lang,
                    consent: true,
                },
                { responseType: "blob" },
            );
            // Derive the filename from Content-Disposition if available,
            // otherwise fall back to a sensible default.
            const cd = res.headers?.["content-disposition"] || "";
            const m = /filename="?([^";]+)"?/i.exec(cd);
            const filename = m ? m[1] : `scan-and-call_${format}.pdf`;
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Small delay so the browser actually starts the download
            // before the blob URL is revoked.
            setTimeout(() => window.URL.revokeObjectURL(url), 4000);
            onDone();
        } catch {
            setErr(t("anforderung_error_save"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            className="mt-8 bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm"
            data-testid="self-print-form"
        >
            <button
                type="button"
                onClick={onBack}
                className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-4"
            >
                <ArrowLeft className="w-4 h-4" />
                {t("anforderung_back_to_method")}
            </button>
            <h2 className="font-display font-bold text-xl sm:text-2xl text-zinc-900 leading-tight">
                {t("anforderung_self_title")}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-zinc-600 leading-relaxed">
                {t("anforderung_self_body").replace("{format}", format)}
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4">
                <label className="block">
                    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                        <Mail className="w-4 h-4 text-[#245288]" />
                        {t("anforderung_field_email")}
                    </span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="self-print-email"
                        placeholder={t("email_popup_email_placeholder")}
                        className="w-full h-12 px-4 rounded-xl bg-zinc-100 border-2 border-transparent focus-visible:border-[#245288] focus-visible:ring-0 focus-visible:outline-none font-display text-base text-zinc-900 placeholder:text-zinc-400"
                    />
                </label>

                <label className="flex items-start gap-3 text-xs sm:text-sm text-zinc-600 cursor-pointer leading-snug">
                    <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        data-testid="self-print-consent"
                        className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-[#245288] focus:ring-[#245288]"
                    />
                    <span>{t("anforderung_self_consent")}</span>
                </label>

                {err && (
                    <div
                        data-testid="self-print-error"
                        className="text-sm text-[#A72920] font-display font-semibold bg-[#A72920]/5 rounded-lg px-3 py-2"
                    >
                        {err}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="self-print-submit"
                    className="w-full h-12 rounded-xl bg-[#245288] hover:bg-[#1b3f6a] disabled:opacity-60 text-white font-display font-black text-base flex items-center justify-center gap-2 transition"
                >
                    {busy ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        t("anforderung_self_submit")
                    )}
                </button>
            </form>
        </section>
    );
};

const ShipForm = ({ format, lang, busy, setBusy, err, setErr, onDone, onBack }) => {
    const { t } = useI18n();
    const [form, setForm] = useState({
        email: "",
        companyName: "",
        contactName: "",
        street: "",
        zip: "",
        city: "",
        country: "Deutschland",
        phone: "",
        quantity: "",
        notes: "",
    });
    const [consent, setConsent] = useState(false);
    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!EMAIL_RE.test(form.email.trim())) {
            setErr(t("anforderung_error_email"));
            return;
        }
        if (
            !form.companyName.trim() ||
            !form.street.trim() ||
            !form.zip.trim() ||
            !form.city.trim()
        ) {
            setErr(t("sticker_error_required"));
            return;
        }
        if (!consent) {
            setErr(t("anforderung_error_consent"));
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/sticker-requests`, {
                ...form,
                email: form.email.trim(),
                companyName: form.companyName.trim(),
                contactName: form.contactName.trim(),
                street: form.street.trim(),
                zip: form.zip.trim(),
                city: form.city.trim(),
                country: form.country.trim(),
                phone: form.phone.trim(),
                quantity: form.quantity ? parseInt(form.quantity, 10) : null,
                notes: form.notes.trim(),
                locale: lang,
                format,
                consent: true,
            });
            onDone();
        } catch {
            setErr(t("anforderung_error_save"));
        } finally {
            setBusy(false);
        }
    };

    const baseInput =
        "w-full h-11 px-3 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400";

    return (
        <section
            className="mt-8 bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm"
            data-testid="ship-form"
        >
            <button
                type="button"
                onClick={onBack}
                className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-4"
            >
                <ArrowLeft className="w-4 h-4" />
                {t("anforderung_back_to_method")}
            </button>
            <h2 className="font-display font-bold text-xl sm:text-2xl text-zinc-900 leading-tight">
                {t("anforderung_ship_title")}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-zinc-600 leading-relaxed">
                {t("anforderung_ship_body").replace("{format}", format)}
            </p>

            <form onSubmit={submit} className="mt-5 space-y-3">
                <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder={t("anforderung_field_email_placeholder")}
                    required
                    data-testid="ship-email"
                    className={baseInput}
                />
                <input
                    type="text"
                    value={form.companyName}
                    onChange={update("companyName")}
                    placeholder={t("sticker_field_company")}
                    required
                    data-testid="ship-company"
                    className={baseInput}
                />
                <input
                    type="text"
                    value={form.contactName}
                    onChange={update("contactName")}
                    placeholder={t("sticker_field_contact")}
                    data-testid="ship-contact"
                    className={baseInput}
                />
                <input
                    type="text"
                    value={form.street}
                    onChange={update("street")}
                    placeholder={t("sticker_field_street")}
                    required
                    data-testid="ship-street"
                    className={baseInput}
                />
                <div className="grid grid-cols-3 gap-2">
                    <input
                        type="text"
                        value={form.zip}
                        onChange={update("zip")}
                        placeholder={t("sticker_field_zip")}
                        required
                        data-testid="ship-zip"
                        className={`col-span-1 ${baseInput}`}
                    />
                    <input
                        type="text"
                        value={form.city}
                        onChange={update("city")}
                        placeholder={t("sticker_field_city")}
                        required
                        data-testid="ship-city"
                        className={`col-span-2 ${baseInput}`}
                    />
                </div>
                <input
                    type="text"
                    value={form.country}
                    onChange={update("country")}
                    placeholder={t("sticker_field_country")}
                    data-testid="ship-country"
                    className={baseInput}
                />
                <div className="grid grid-cols-2 gap-2">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder={t("sticker_field_phone")}
                        data-testid="ship-phone"
                        className={baseInput}
                    />
                    <input
                        type="number"
                        min="1"
                        value={form.quantity}
                        onChange={update("quantity")}
                        placeholder={t("sticker_field_quantity")}
                        data-testid="ship-quantity"
                        className={baseInput}
                    />
                </div>
                <textarea
                    value={form.notes}
                    onChange={update("notes")}
                    rows={2}
                    placeholder={t("sticker_field_notes")}
                    data-testid="ship-notes"
                    className="w-full px-3 py-2 rounded-lg bg-zinc-100 border-2 border-transparent focus-visible:border-emerald-600 focus-visible:ring-0 focus-visible:outline-none text-zinc-900 placeholder:text-zinc-400 resize-none"
                />

                <label className="flex items-start gap-3 text-xs sm:text-sm text-zinc-600 cursor-pointer leading-snug">
                    <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        data-testid="ship-consent"
                        className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-600"
                    />
                    <span>{t("email_popup_consent")}</span>
                </label>

                {err && (
                    <div
                        data-testid="ship-error"
                        className="text-sm text-[#A72920] font-display font-semibold bg-[#A72920]/5 rounded-lg px-3 py-2"
                    >
                        {err}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="ship-submit"
                    className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-display font-black text-base flex items-center justify-center gap-2 transition"
                >
                    {busy ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        t("anforderung_ship_submit")
                    )}
                </button>
            </form>
        </section>
    );
};

const SuccessCard = ({ title, body, testid }) => {
    const { t } = useI18n();
    return (
        <section
            className="mt-8 bg-white border border-emerald-200 rounded-2xl p-8 sm:p-10 shadow-sm text-center"
            data-testid={testid}
        >
            <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-600" strokeWidth={3} />
            </div>
            <h2 className="mt-4 font-display font-black text-2xl sm:text-3xl text-zinc-900">
                {title}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-zinc-600 max-w-md mx-auto leading-relaxed">
                {body}
            </p>
            <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-[#245288] hover:text-[#1a3e6b] font-display font-bold transition"
            >
                <ArrowLeft className="w-4 h-4" />
                <span>{t("anforderung_back_home")}</span>
            </Link>
        </section>
    );
};

const Header = () => (
    <header className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
            <Link
                to="/"
                className="flex items-center gap-2 font-display font-black text-zinc-900 text-lg sm:text-xl"
            >
                <FirstAidSymbol size={28} />
                SafetyAssist
            </Link>
            <LanguagePicker />
        </div>
    </header>
);
